/**
 * Recipe React Query Hooks
 *
 * With Better Auth, session cookies are sent automatically.
 * No token passing needed - just call the API functions directly.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import * as recipeApi from "../api/recipes";
import { track } from "../analytics";
import type { Recipe, RecipeFilters } from "../api/types";
import type {
  RecipeCreateInput,
  RecipeUpdateInput,
} from "@batch-and-bowl/validators";

export const RECIPES_QUERY_KEY = "recipes";
export const FAVORITES_QUERY_KEY = "favorites";

/**
 * Fetch recipes with filters (returns just array for backward compatibility)
 */
export function useRecipes(filters?: RecipeFilters) {
  return useQuery({
    queryKey: [RECIPES_QUERY_KEY, filters],
    queryFn: () => recipeApi.getRecipes(filters),
  });
}

/**
 * Fetch recipes with pagination info
 */
export function useRecipesWithPagination(filters?: RecipeFilters) {
  return useQuery({
    queryKey: [RECIPES_QUERY_KEY, "paginated", filters],
    queryFn: () => recipeApi.getRecipesWithPagination(filters),
  });
}

/**
 * Fetch a single recipe
 */
export function useRecipe(id: string, proteinChoiceId?: string | null) {
  return useQuery({
    queryKey: proteinChoiceId ? [RECIPES_QUERY_KEY, id, { proteinChoiceId }] : [RECIPES_QUERY_KEY, id],
    queryFn: () => proteinChoiceId ? recipeApi.getRecipe(id, proteinChoiceId) : recipeApi.getRecipe(id),
    enabled: !!id,
  });
}

/**
 * Fetch favorite recipes. scope defaults to "mine"; "household" returns
 * the union of favorites across the caller's household with per-recipe
 * attribution (Recipe.favoritedBy). scope is part of the query key so
 * "mine" and "household" cache independently.
 */
export function useFavoriteRecipes(scope: "mine" | "household" = "mine") {
  return useQuery({
    queryKey: [FAVORITES_QUERY_KEY, scope],
    queryFn: () => recipeApi.getFavoriteRecipes(scope),
  });
}

/**
 * Get the authenticated user's own recipes, optionally filtered by status.
 */
export function useMyRecipes(status?: "draft" | "published") {
  return useQuery({
    queryKey: [RECIPES_QUERY_KEY, "mine", status ?? "all"],
    queryFn: () => recipeApi.getMyRecipes(status),
  });
}

/**
 * Fetch recipes with infinite scroll
 */
export function useInfiniteRecipes(
  filters?: Omit<recipeApi.RecipeCollectionFilters, "page" | "limit">,
) {
  return useInfiniteQuery({
    queryKey: [RECIPES_QUERY_KEY, "infinite", filters],
    queryFn: ({ pageParam = 1 }) =>
      recipeApi.getRecipesWithPagination({
        ...filters,
        page: pageParam,
        limit: 12,
      }),
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    initialPageParam: 1,
  });
}

/**
 * Toggle recipe favorite
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recipeId: string) => recipeApi.toggleRecipeFavorite(recipeId),
    onMutate: async (recipeId) => {
      // Cancel any outgoing refetches for this recipe
      await queryClient.cancelQueries({
        queryKey: [RECIPES_QUERY_KEY, recipeId],
      });

      // Snapshot the previous value
      const previousRecipe = queryClient.getQueryData([
        RECIPES_QUERY_KEY,
        recipeId,
      ]);

      // Optimistically update the recipe
      queryClient.setQueryData(
        [RECIPES_QUERY_KEY, recipeId],
        (old: Recipe | undefined) => {
          if (!old) return old;
          return {
            ...old,
            isFavorited: !old.isFavorited,
          };
        },
      );

      return { previousRecipe };
    },
    onError: (_err, recipeId, context) => {
      // Rollback on error
      if (context?.previousRecipe) {
        queryClient.setQueryData(
          [RECIPES_QUERY_KEY, recipeId],
          context.previousRecipe,
        );
      }
    },
    onSuccess: (data, recipeId) => {
      // Fired here, not in onMutate: the optimistic flip above is rolled back
      // on failure, so only the server's confirmed { favorited } is a real
      // toggle. Boolean only -- no recipe id or title in the props.
      track("favorite_toggled", { favorited: data.favorited });

      // Invalidate favorites list to refetch
      queryClient.invalidateQueries({ queryKey: [FAVORITES_QUERY_KEY] });
      // Invalidate the specific recipe to update isFavorited field
      queryClient.invalidateQueries({
        queryKey: [RECIPES_QUERY_KEY, recipeId],
      });
      // Invalidate all recipe lists to update heart icons everywhere
      queryClient.invalidateQueries({ queryKey: [RECIPES_QUERY_KEY] });
    },
  });
}

/**
 * Create a new recipe
 */
export function useCreateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RecipeCreateInput) => recipeApi.createRecipe(data),
    onSuccess: () => {
      // Invalidate all recipe queries to show the new recipe
      queryClient.invalidateQueries({ queryKey: [RECIPES_QUERY_KEY] });
    },
  });
}

/**
 * Update an existing recipe
 */
export function useUpdateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RecipeUpdateInput }) =>
      recipeApi.updateRecipe(id, data),
    onSuccess: (updatedRecipe) => {
      // Invalidate the specific recipe
      queryClient.invalidateQueries({
        queryKey: [RECIPES_QUERY_KEY, updatedRecipe.id],
      });
      // Invalidate all recipe lists
      queryClient.invalidateQueries({ queryKey: [RECIPES_QUERY_KEY] });
    },
  });
}

/**
 * Publish a recipe
 */
export function usePublishRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recipeApi.publishRecipe(id),
    onSuccess: (publishedRecipe) => {
      // Invalidate the specific recipe
      queryClient.invalidateQueries({
        queryKey: [RECIPES_QUERY_KEY, publishedRecipe.id],
      });
      // Invalidate all recipe lists
      queryClient.invalidateQueries({ queryKey: [RECIPES_QUERY_KEY] });
    },
  });
}

/**
 * Fetch published-catalog recipe counts per dietary tag.
 * Query key is order-independent (tags are sorted) so the same set of tags
 * always hits the same cache entry regardless of caller ordering.
 */
export function useTagCounts(tags: string[]) {
  return useQuery({
    queryKey: ["tag-counts", ...[...tags].sort()],
    queryFn: () => recipeApi.getTagCounts(tags),
    enabled: tags.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}
